import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Grid, Card, CardContent, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { getArticles } from '../../utils/requests';
import dayjs from 'dayjs';

// Styled components with responsive support
const SectionTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isTablet' && prop !== 'isMobile',
})<{ isTablet?: boolean; isMobile?: boolean }>(({ isTablet, isMobile }) => ({
  fontSize: isMobile ? '28px' : (isTablet ? '40px' : '40px'), // ปรับขนาดฟอนต์สำหรับ mobile
  fontWeight: 500,
  lineHeight: 1.25,
}));

const ViewAllButton = styled(Button)({
  fontSize: '16px',
  color: '#333',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
  },
});

const ArticleCard = styled(Card)({
  boxShadow: 'none',
  borderRadius: '10px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  transition: 'all 0.3s ease',
  '&:hover': {
    '& img': {
      transform: 'scale(1.05)',
    },
    '& .article-title': {
      color: '#F4762A',
    }
  }
});

const ArticleImage = styled('img')({
  width: '100%',
  borderRadius: '10px',
  objectFit: 'cover',
  transition: 'transform 0.3s ease',
});

const ArticleTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isTablet' && prop !== 'isMobile' && prop !== 'isMain',
})<{ isTablet?: boolean; isMobile?: boolean; isMain?: boolean }>(({ isTablet, isMobile, isMain }) => ({
  fontSize: isMobile ? (isMain ? '19px' : '16px') : (isTablet ? (isMain ? '23px' : '16px') : '23px'), // ปรับขนาดฟอนต์สำหรับ mobile
  fontWeight: 500,
  lineHeight: isMobile ? '125%' : (isTablet ? (isMain ? '130%' : '125%') : '130%'),
  marginBottom: '8px',
  transition: 'color 0.3s ease',
}));

const ArticleDescription = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile?: boolean }>(({ isMobile }) => ({
  fontSize: isMobile ? '14px' : '16px', // ปรับขนาดฟอนต์สำหรับ mobile
  fontWeight: 400,
  color: '#717172',
  marginBottom: '16px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
}));

const ArticleDate = styled(Typography)({
  fontSize: '14px',
  fontWeight: 400,
  color: '#9F9FA0',
  position: 'absolute',
  bottom: '16px',
  left: '16px',
});

// Interface for article item
interface ArticleItem {
  id: number;
  image: string;
  title: string;
  description: string;
  date: string;
  link: string;
}

interface ApiArticleItem {
  news_ID: number;
  news_Images: string;
  news_Title_TH: string;
  news_Title_EN: string;
  news_Title_CN: string;
  news_Des_TH: string;
  news_Des_EN: string;
  news_Des_CN: string;
  news_Create: string;
  news_Update: string;
  category_ID: number;
  [key: string]: any;
}

interface NewsArticleSectionProps {
  viewAllLink?: string;
  articles?: ArticleItem[];
  isTablet?: boolean;
  isMobile?: boolean;
}

const NewsArticleSection: React.FC<NewsArticleSectionProps> = ({ 
  viewAllLink = "/article",
  articles = [],
  isTablet = false,
  isMobile = false
}) => {
  const { t, i18n } = useTranslation(['home']);
  const [loading, setLoading] = useState<boolean>(true);
  const [apiArticles, setApiArticles] = useState<ArticleItem[]>([]);
  const locale = i18n.language || 'th';

  // Define responsive dimensions with mobile support
  const mainImageHeight = isMobile ? 'auto' : (isTablet ? 564 : 574); // auto height สำหรับ mobile เพื่อใช้ aspect ratio
  const secondaryImageHeight = isMobile ? 'auto' : (isTablet ? 225 : 228); // auto height สำหรับ mobile เพื่อใช้ aspect ratio
  const secondaryCardHeight = isMobile ? 'auto' : (isTablet ? 377 : 396); // auto height สำหรับ mobile

  // 1. ใช้ React.useMemo เพื่อไม่ให้สร้าง defaultArticles ใหม่ทุกครั้งที่ render
  const defaultArticles = React.useMemo<ArticleItem[]>(() => [
    {
      id: 1,
      image: '/assets/homePage/mock_article1.webp',
      title: t('news_articles.default_articles.article1.title'),
      description: t('news_articles.default_articles.article1.description'),
      date: t('news_articles.default_articles.article1.date'),
      link: '/article/1'
    },
    {
      id: 2,
      image: '/assets/homePage/mock_article2.webp',
      title: t('news_articles.default_articles.article2.title'),
      description: t('news_articles.default_articles.article2.description'),
      date: t('news_articles.default_articles.article2.date'),
      link: '/article/2'
    },
    {
      id: 3,
      image: '/assets/homePage/mock_article3.webp',
      title: t('news_articles.default_articles.article3.title'),
      description: t('news_articles.default_articles.article3.description'),
      date: t('news_articles.default_articles.article3.date'),
      link: '/article/3'
    },
    {
      id: 4,
      image: '/assets/homePage/mock_article4.webp',
      title: t('news_articles.default_articles.article4.title'),
      description: t('news_articles.default_articles.article4.description'),
      date: t('news_articles.default_articles.article4.date'),
      link: '/article/4'
    },
    {
      id: 5,
      image: '/assets/homePage/mock_article5.webp',
      title: t('news_articles.default_articles.article5.title'),
      description: t('news_articles.default_articles.article5.description'),
      date: t('news_articles.default_articles.article5.date'),
      link: '/article/5'
    },
  ], [t]);

  // 2. ใช้ useEffect เพื่อดึงข้อมูลบทความจาก API เมื่อคอมโพเนนต์ถูกโหลด
  useEffect(() => {
    // 3. ใช้ AbortController เพื่อยกเลิกการร้องขอเมื่อคอมโพเนนต์ถูก unmount
    const controller = new AbortController();
    
    const fetchArticles = async () => {
      try {
        setLoading(true);
        // 4. ใช้ getArticles API ด้วยพารามิเตอร์ที่กำหนด
        const response = await getArticles(1, 5);
        
        if (response && response.data) {
          // 5. แปลงข้อมูลจาก API เป็นรูปแบบที่คอมโพเนนต์ต้องการ
          const formattedArticles: ArticleItem[] = response.data.map((article: ApiArticleItem) => {
            const langUpper = locale.toUpperCase();
            return {
              id: article.news_ID,
              image: article.news_Images ? `https://cdn.9asset.com/download/article_image/${article.news_ID}/${article.news_Images}` : '/assets/single-logo.png',
              title: article[`news_Title_${langUpper}`] || article.news_Title_TH,
              description: article[`news_Des_${langUpper}`] || article.news_Des_TH,
              date: dayjs(article.news_Create).format('DD MMMM YYYY'),
              link: `/article/${article.news_ID}/${encodeURIComponent(article[`news_Title_${langUpper}`] || article.news_Title_TH).replace(/%20/g, '-')}`
            };
          });
          
          setApiArticles(formattedArticles);
        }
      } catch (error) {
        console.error('Failed to fetch articles:', error);
        // ในกรณีที่เกิดข้อผิดพลาด ใช้ข้อมูลเริ่มต้น
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();

    // 6. ทำความสะอาดเมื่อคอมโพเนนต์ถูก unmount
    return () => {
      controller.abort();
    };
  }, [locale]); // 7. ดึงข้อมูลใหม่เมื่อภาษาเปลี่ยน

  // 8. ตัดสินใจว่าจะใช้ข้อมูลจากไหน โดยมีลำดับความสำคัญ: articles props > API data > default data
  const displayArticles = articles.length > 0 ? articles : (apiArticles.length > 0 ? apiArticles : defaultArticles);

  const handleViewAll = () => {
    window.location.href = viewAllLink;
  };

  // 9. ใช้ React.useMemo เพื่อไม่ให้คำนวณ mainArticle และ secondaryArticles ใหม่ทุกครั้งที่ render
  const [mainArticle, secondaryArticles] = React.useMemo(() => {
    const main = displayArticles[0];
    const secondary = displayArticles.slice(1, 5);
    return [main, secondary];
  }, [displayArticles]);

  // 10. แสดง skeleton ในขณะที่กำลังโหลดข้อมูล
  if (loading) {
    return (
      <Box sx={{ 
        backgroundColor: '#FFFFFF',
        padding: '80px 0',
      }}>
        <Box sx={{ 
          maxWidth: '1496px',
          margin: '0 auto',
          padding: '0',
          height: '832px'
        }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '40px'
          }}>
            <Skeleton variant="text" width={300} height={60} />
            <Skeleton variant="text" width={150} height={40} />
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ height: '574px', borderRadius: '10px' }}>
                <Skeleton variant="rectangular" width="100%" height={574} />
              </Box>
              <Box sx={{ padding: '24px 16px' }}>
                <Skeleton variant="text" width="80%" height={60} />
                <Skeleton variant="text" width="100%" height={80} />
                <Skeleton variant="text" width="40%" height={30} />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Grid container spacing={4}>
                {[1, 2, 3, 4].map((item) => (
                  <Grid item xs={12} sm={6} key={item}>
                    <Box sx={{ height: '228px', borderRadius: '10px' }}>
                      <Skeleton variant="rectangular" width="100%" height={228} />
                    </Box>
                    <Box sx={{ padding: '16px' }}>
                      <Skeleton variant="text" width="80%" height={50} />
                      <Skeleton variant="text" width="100%" height={60} />
                      <Skeleton variant="text" width="40%" height={30} />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: '#FFFFFF',
      padding: isMobile ? '48px 16px' : (isTablet ? '64px 57px' : '80px 0'), // ปรับ padding สำหรับ mobile
    }}>
      <Box sx={{ 
        maxWidth: '1496px',
        margin: '0 auto',
        padding: '0',
        height: isMobile ? 'auto' : (isTablet ? 'auto' : '832px') // auto height สำหรับ mobile
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: isMobile ? 'center' : 'space-between', // จัดกลางใน mobile
          alignItems: 'center',
          marginBottom: isMobile ? '24px' : '40px' // ปรับ margin สำหรับ mobile
        }}>
          <SectionTitle isTablet={isTablet} isMobile={isMobile}>{t('news_articles.title')}</SectionTitle>
          {!isMobile && ( // ซ่อนปุ่มใน mobile
            <ViewAllButton 
              endIcon={<ArrowCircleRightOutlinedIcon sx={{ color: '#F4762A' }} />} 
              onClick={handleViewAll}
            >
              {t('news_articles.view_all')}
            </ViewAllButton>
          )}
        </Box>

        <Grid container spacing={isMobile ? 2 : 4}> {/* ปรับ spacing สำหรับ mobile */}
          {/* Main Article */}
          <Grid item xs={12} md={6}>
            <Link href={mainArticle.link} passHref style={{ textDecoration: 'none', color: 'inherit' }}>
              <ArticleCard>
                <Box sx={{ 
                  height: mainImageHeight, 
                  overflow: 'hidden', 
                  borderRadius: '10px',
                  aspectRatio: isMobile ? '4/3' : 'auto', // 4:3 aspect ratio สำหรับ mobile
                  width: '100%'
                }}>
                  <ArticleImage
                    src={mainArticle.image}
                    alt={mainArticle.title}
                    height={mainImageHeight}
                    loading="lazy" // 11. ใช้ lazy loading สำหรับรูปภาพ
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      e.currentTarget.src = '/assets/single-logo.png'; // 12. กำหนดรูปภาพสำรองเมื่อโหลดรูปผิดพลาด
                    }}
                  />
                </Box>
                <CardContent sx={{ padding: isMobile ? '16px 12px' : '24px 16px', flex: 1, position: 'relative', pb: '60px' }}> {/* ปรับ padding สำหรับ mobile */}
                  <ArticleTitle className="article-title" isTablet={isTablet} isMobile={isMobile} isMain>{mainArticle.title}</ArticleTitle>
                  <ArticleDescription isMobile={isMobile}>{mainArticle.description}</ArticleDescription>
                  <ArticleDate>{mainArticle.date}</ArticleDate>
                </CardContent>
              </ArticleCard>
            </Link>
          </Grid>

          {/* Secondary Articles */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={isMobile ? 2 : 4}> {/* ปรับ spacing สำหรับ mobile */}
              {secondaryArticles.map((article, index) => (
                <Grid item xs={12} sm={isMobile ? 12 : 6} key={article.id}> {/* ทำให้เป็น 100% width ใน mobile */}
                  <Link href={article.link} passHref style={{ textDecoration: 'none', color: 'inherit' }}>
                    <ArticleCard sx={{ height: secondaryCardHeight }}>
                      <Box sx={{ 
                        height: secondaryImageHeight, 
                        overflow: 'hidden', 
                        borderRadius: '10px',
                        aspectRatio: isMobile ? '4/3' : 'auto', // 4:3 aspect ratio สำหรับ mobile
                        width: '100%'
                      }}>
                        <ArticleImage
                          src={article.image}
                          alt={article.title}
                          height={secondaryImageHeight}
                          loading="lazy" // 13. ใช้ lazy loading สำหรับรูปภาพ
                          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                            e.currentTarget.src = '/assets/single-logo.png'; // 14. กำหนดรูปภาพสำรองเมื่อโหลดรูปผิดพลาด
                          }}
                        />
                      </Box>
                      <CardContent sx={{ padding: isMobile ? '12px' : '16px', flex: 1, position: 'relative', pb: '60px' }}> {/* ปรับ padding สำหรับ mobile */}
                        <ArticleTitle className="article-title" isTablet={isTablet} isMobile={isMobile} isMain={false}>{article.title}</ArticleTitle>
                        <ArticleDescription isMobile={isMobile}>{article.description}</ArticleDescription>
                        <ArticleDate>{article.date}</ArticleDate>
                      </CardContent>
                    </ArticleCard>
                  </Link>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        {/* ปุ่ม "ดูทั้งหมด" สำหรับ mobile อยู่ตรงกลางด้านล่าง */}
        {isMobile && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginTop: '32px' 
          }}>
            <ViewAllButton 
              endIcon={<ArrowCircleRightOutlinedIcon sx={{ color: '#F4762A' }} />} 
              onClick={handleViewAll}
            >
              {t('news_articles.view_all')}
            </ViewAllButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export { NewsArticleSection };
export default NewsArticleSection; 